// API Configuration
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL
  : process.env.REACT_APP_LOCAL_API_URL;

// Token management
const TOKEN_KEY = 'sul_investimentos_token';
const REFRESH_TOKEN_KEY = 'sul_investimentos_refresh_token';
const TOKEN_TIMESTAMP_KEY = 'sul_investimentos_token_timestamp';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);
export const getTokenTimestamp = () => {
  const timestamp = localStorage.getItem(TOKEN_TIMESTAMP_KEY);
  return timestamp ? parseInt(timestamp) : null;
};

export const setTokens = (token, refreshToken) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  localStorage.setItem(TOKEN_TIMESTAMP_KEY, Date.now().toString());
};

export const removeTokens = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(TOKEN_TIMESTAMP_KEY);
};

// Check if token is about to expire (within 1 minute)
export const isTokenExpiringSoon = () => {
  const timestamp = getTokenTimestamp();
  if (!timestamp) return true;
  
  const tokenAge = Date.now() - timestamp;
  const tokenAgeMinutes = tokenAge / (1000 * 60);
  
  // Token expires at 15 minutes, consider it expiring soon at 14 minutes
  return tokenAgeMinutes >= 14;
};

// API request helper
export const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const token = getToken();

  // Check if body is FormData - don't set Content-Type, let browser set it with boundary
  const isFormData = options.body instanceof FormData;

  const config = {
    ...options,
    headers: {
      // Only set Content-Type for non-FormData requests
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...options.headers,
    },
  };
  
  // Add authorization header if token exists
  if (token && !options.skipAuth) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  try {
    const response = await fetch(url, config);

    // Handle blob responses (for file downloads like PDF reports)
    if (options.responseType === 'blob') {
      if (!response.ok) {
        throw new Error('Erro ao baixar arquivo');
      }
      return await response.blob();
    }

    const data = await response.json();

    // If token expired, try to refresh
    if (response.status === 401 && !options.skipAuth) {
      const refreshToken = getRefreshToken();
      if (refreshToken && !endpoint.includes('refresh')) {
        const refreshed = await refreshTokens();
        if (refreshed) {
          // Retry original request with new token
          config.headers.Authorization = `Bearer ${getToken()}`;
          const retryResponse = await fetch(url, config);
          return await retryResponse.json();
        }
      }
    }
    
    if (!response.ok) {
      throw new Error(data.message || 'Erro na requisição');
    }
    
    return data;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// Refresh tokens
export const refreshTokens = async () => {
  try {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return false;
    
    const response = await apiRequest('/auth/refresh-token', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
      skipAuth: true,
    });
    
    if (response.success) {
      setTokens(response.data.accessToken, response.data.refreshToken);
      return true;
    }
    
    return false;
  } catch (error) {
    removeTokens();
    return false;
  }
};

// Create api object with HTTP methods
const api = {
  get: (endpoint, options = {}) => 
    apiRequest(endpoint, { ...options, method: 'GET' }),
  
  post: (endpoint, data, options = {}) =>
    apiRequest(endpoint, {
      ...options,
      method: 'POST',
      body: data instanceof FormData ? data : JSON.stringify(data)
    }),
  
  put: (endpoint, data, options = {}) =>
    apiRequest(endpoint, {
      ...options,
      method: 'PUT',
      body: data instanceof FormData ? data : JSON.stringify(data)
    }),
  
  delete: (endpoint, options = {}) => 
    apiRequest(endpoint, { ...options, method: 'DELETE' }),
  
  patch: (endpoint, data, options = {}) =>
    apiRequest(endpoint, {
      ...options,
      method: 'PATCH',
      body: data instanceof FormData ? data : JSON.stringify(data)
    })
};

// Export api object as default for use in components
export default api;

// Also export API_BASE_URL for backward compatibility
export { API_BASE_URL };
